import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Globe, ArrowRight, Clock, DollarSign, ExternalLink, 
  ChevronRight, Check, AlertCircle, Users, FileText, MapPin, Camera
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OCIPage = () => {
  const [ociData, setOciData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOCIData();
  }, []);

  const fetchOCIData = async () => {
    try {
      const response = await axios.get(`${API}/oci`);
      setOciData(response.data);
    } catch (error) {
      console.error("Error fetching OCI data:", error);
      toast.error("Failed to load OCI information");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#E15636] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#64748B]">Loading OCI information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 md:pt-32 pb-20" data-testid="oci-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-8">
        <div className="flex items-center gap-2 text-sm text-[#64748B]">
          <Link to="/" className="hover:text-[#0F172A]">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-[#0F172A] font-medium">OCI Services</span>
        </div>
      </div>

      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-12">
        <div className="flex flex-col md:flex-row items-start gap-6">
          <div className="w-20 h-20 bg-[#FF9933] rounded-2xl flex items-center justify-center">
            <span className="text-4xl">🇮🇳</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 text-[#E15636] mb-2">
              <Users className="w-5 h-5" />
              <span className="text-sm font-medium uppercase tracking-widest">For Indians in Canada</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-heading tracking-tight text-[#0F172A] mb-4">
              {ociData?.name || "OCI Card Services"}
            </h1>
            <p className="text-lg text-[#64748B] max-w-2xl">
              {ociData?.description}
            </p>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 text-[#64748B] text-sm mb-2">
              <DollarSign className="w-4 h-4" />
              Fee
            </div>
            <p className="text-2xl font-heading text-[#0F172A]">CAD ${ociData?.fee_cad}</p>
            <p className="text-xs text-[#64748B]">Canadian Dollars</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 text-[#64748B] text-sm mb-2">
              <Clock className="w-4 h-4" />
              Processing
            </div>
            <p className="text-xl font-heading text-[#0F172A]">{ociData?.processing_time}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 text-[#64748B] text-sm mb-2">
              <Globe className="w-4 h-4" />
              Validity
            </div>
            <p className="text-xl font-heading text-[#0F172A]">{ociData?.validity}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <a 
              href={ociData?.official_portal}
              target="_blank"
              rel="noopener noreferrer"
              className="block h-full"
            >
              <Button className="w-full h-full rounded-xl bg-[#E15636] hover:bg-[#C24125] flex-col gap-2">
                <ExternalLink className="w-5 h-5" />
                <span>Apply Online</span>
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Benefits */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8" data-testid="oci-benefits">
              <h2 className="text-xl font-heading text-[#0F172A] mb-6 flex items-center gap-2">
                <Check className="w-5 h-5 text-emerald-500" />
                Benefits of OCI Card
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ociData?.benefits?.map((benefit, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl"
                  >
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-emerald-800">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs for Eligibility and Documents */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <Tabs defaultValue="eligibility" className="w-full">
                <TabsList className="w-full justify-start border-b border-slate-200 rounded-none bg-slate-50 p-0 h-auto">
                  <TabsTrigger 
                    value="eligibility" 
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E15636] data-[state=active]:bg-white px-6 py-4"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Eligibility
                  </TabsTrigger>
                  <TabsTrigger 
                    value="documents" 
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E15636] data-[state=active]:bg-white px-6 py-4"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Documents
                  </TabsTrigger>
                  <TabsTrigger 
                    value="photo" 
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E15636] data-[state=active]:bg-white px-6 py-4"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Photo Specs
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="eligibility" className="p-6 md:p-8 mt-0">
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-heading text-lg text-[#0F172A] mb-4">Who Can Apply</h3>
                      <div className="space-y-3">
                        {ociData?.eligibility?.map((item, index) => (
                          <div 
                            key={index}
                            className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg"
                          >
                            <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <p className="text-[#0F172A]">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-heading text-lg text-red-600 mb-4">Not Eligible</h3>
                      <div className="space-y-3">
                        {ociData?.not_eligible?.map((item, index) => (
                          <div 
                            key={index}
                            className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg"
                          >
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-red-800">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="documents" className="p-6 md:p-8 mt-0">
                  <div className="space-y-4">
                    {ociData?.documents_required?.map((doc, index) => (
                      <div 
                        key={index}
                        className={`flex items-start gap-4 p-4 rounded-xl bg-slate-50 border ${
                          doc.mandatory ? 'border-l-4 border-l-[#E15636] border-slate-200' : 'border-slate-200'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          doc.mandatory ? 'bg-[#E15636] text-white' : 'bg-slate-200 text-slate-600'
                        }`}>
                          <Check className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="font-medium text-[#0F172A] mb-1">
                            {doc.name}
                            {doc.mandatory && <span className="text-red-500 ml-1">*</span>}
                          </h4>
                          <p className="text-sm text-[#64748B]">{doc.description}</p>
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-[#64748B] mt-4">* Required documents</p>
                  </div>
                </TabsContent>

                <TabsContent value="photo" className="p-6 md:p-8 mt-0">
                  {ociData?.photo_spec && (
                    <div className="flex items-start gap-6">
                      <div className="w-24 h-24 bg-white border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center">
                        <Camera className="w-8 h-8 text-slate-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-heading text-[#0F172A] mb-4">Photo Requirements</h3>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-[#64748B]">Size</p>
                            <p className="font-medium text-[#0F172A]">{ociData.photo_spec.width} x {ociData.photo_spec.height}</p>
                          </div>
                          <div>
                            <p className="text-sm text-[#64748B]">Background</p>
                            <p className="font-medium text-[#0F172A]">{ociData.photo_spec.background}</p>
                          </div>
                        </div>
                        {ociData.photo_spec.additional_notes?.length > 0 && (
                          <div>
                            <p className="text-sm text-[#64748B] mb-2">Additional Requirements:</p>
                            <ul className="space-y-1">
                              {ociData.photo_spec.additional_notes.map((note, index) => (
                                <li key={index} className="flex items-start gap-2 text-sm text-[#0F172A]">
                                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                  {note}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Right Column - Canada Specific */}
          <div className="space-y-8">
            {/* Apply in Canada */}
            <div className="bg-[#0F172A] rounded-2xl p-6 text-white" data-testid="apply-canada">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">🇨🇦</span>
                <h3 className="text-lg font-heading">Apply in Canada</h3>
              </div>
              <p className="text-slate-300 text-sm mb-4">
                Submit your OCI application through {ociData?.canada_specific?.submission_center}
              </p>
              <a 
                href={ociData?.canada_specific?.portal}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="w-full rounded-full bg-[#E15636] hover:bg-[#C24125]">
                  BLS Canada Portal
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>

            {/* Submission Locations */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="locations">
              <h3 className="text-lg font-heading text-[#0F172A] mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#E15636]" />
                Submission Locations
              </h3>
              <div className="space-y-4">
                {ociData?.canada_specific?.consulates?.map((consulate, index) => (
                  <div 
                    key={index}
                    className="p-4 bg-slate-50 rounded-xl"
                  >
                    <h4 className="font-medium text-[#0F172A]">{consulate.city}</h4>
                    <p className="text-sm text-[#64748B]">{consulate.jurisdiction}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-heading text-lg text-amber-800 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Important Notes
              </h3>
              <ul className="space-y-2">
                {ociData?.canada_specific?.notes?.map((note, index) => (
                  <li key={index} className="text-sm text-amber-700 flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {note}
                  </li>
                ))}
              </ul>
            </div>

            {/* Official Portal */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-heading text-[#0F172A] mb-4">Official Portal</h3>
              <a 
                href={ociData?.official_portal}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-4 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
              >
                <span className="flex items-center gap-2 text-[#0F172A]">
                  <Globe className="w-5 h-5" />
                  OCI Services India
                </span>
                <ExternalLink className="w-4 h-4 text-[#64748B]" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OCIPage;
